import React from "react";
import { FaCopy } from "react-icons/fa";
import { notify } from "../Notifications/notify";

/**
 * CopyButton - Standardized copy-to-clipboard button component
 *
 * Features:
 * - Uses react-icons FaCopy icon
 * - Integrates with app's notification system
 * - Handles clipboard API errors gracefully
 * - Supports custom text and styling
 * - Provides visual feedback via toast notifications
 */
export const CopyButton = ({
  text,
  onCopy,
  variant = "soft", // "soft" | "icon" | "primary"
  size = "sm",
  className = "",
  disabled = false,
  title = "Copy to clipboard",
  ...props
}) => {
  const handleCopy = async () => {
    try {
      const textToCopy = text || onCopy?.();
      if (!textToCopy) {
        notify.error("Nothing to copy");
        return;
      }

      await navigator.clipboard.writeText(textToCopy);
      notify.success("Copied to clipboard");
    } catch (error) {
      console.error("Failed to copy to clipboard:", error);
      notify.error("Failed to copy to clipboard");
    }
  };

  // Icon-only variant (minimal)
  if (variant === "icon") {
    return (
      <button
        onClick={handleCopy}
        disabled={disabled}
        className={`
          inline-flex items-center justify-center min-h-11 min-w-11 p-2 text-brand-muted hover:text-brand-charcoal hover:bg-brand-charcoal/5 
          rounded-lg transition-colors duration-200
          disabled:opacity-50 disabled:cursor-not-allowed
          ${className}
        `}
        title={title}
        {...props}
      >
        <FaCopy className="w-4 h-4" />
      </button>
    );
  }

  // Primary variant (blue button)
  if (variant === "primary") {
    return (
      <button
        onClick={handleCopy}
        disabled={disabled}
        className={`
          px-4 py-2 bg-brand-gold text-brand-charcoal rounded-lg text-sm font-display font-bold
          hover:bg-brand-gold/90 focus:outline-none focus:ring-2 focus:ring-brand-gold
          transition-colors duration-200
          disabled:opacity-50 disabled:cursor-not-allowed
          flex items-center gap-2
          ${className}
        `}
        title={title}
        {...props}
      >
        <FaCopy className="w-4 h-4" />
        Copy
      </button>
    );
  }

  // Soft variant (default - matches existing SoftButton style)
  const sizeClasses = {
    sm: "px-3 py-1.5 text-sm",
    xs: "px-2 py-1 text-sm",
  };

  return (
    <button
      onClick={handleCopy}
      disabled={disabled}
      className={`
        inline-flex items-center justify-center font-display font-bold tracking-wide rounded-lg 
        transition-colors disabled:opacity-60 disabled:cursor-not-allowed
        text-brand-charcoal bg-white border border-brand-charcoal/20 
        hover:border-brand-gold-ink
        ${sizeClasses[size]}
        ${className}
      `}
      title={title}
      {...props}
    >
      <FaCopy className="w-4 h-4 mr-1.5" />
      Copy Link
    </button>
  );
};

export default CopyButton;
